import dayjs from 'dayjs'
import { AppMessageConditionType } from '@pushly/models/lib/enums/app-messages/app-message-condition-type'
import { AppMessageActionType } from '@pushly/models/lib/enums/app-messages/app-message-action-type'
import { AppMessageBuilderState } from './interfaces/app-message-builder-state.interface'

/** The three expandable rows of the behaviour card. */
export type BehaviorSummaryRowKey = 'trigger' | 'onScreen' | 'schedule'

export type ReadinessCheck = {
    key: string
    label: string
    satisfied: boolean
    /**
     * The behaviour row holding the controls for this check. A failing check
     * with a row named here can open it, so the fix is where the problem is
     * reported rather than somewhere the reader has to go looking.
     */
    revealRow?: BehaviorSummaryRowKey
}

export type Readiness = {
    checks: ReadinessCheck[]
    blockingCount: number
    ready: boolean
}

/**
 * The content fields and buttons that can each satisfy the "has something to
 * show" requirement on their own.
 */
const CONTENT_FIELDS = ['body', 'image', 'title', 'header', 'ctaPrimary', 'ctaSecondary'] as const

const MARKUP_TAGS = /(<([^>]+)>)/gi

const GROUPED_NUMBER = new Intl.NumberFormat('en-US')

/** Rich text arrives as markup, so emptiness has to be judged on the text inside it. */
function hasVisibleText(markup: string | null | undefined): boolean {
    return !!markup
        ?.replace(MARKUP_TAGS, '')
        .replace(/&nbsp;/gi, ' ')
        .trim()
}

/**
 * Everything standing between the form and a successful submit, in the order a
 * reader works down the page.
 *
 * Follows what `cleanAndValidateMessage` and `cleanAndValidateTemplate` throw
 * on, so the panel cannot report a message ready and have the save reject it. A
 * check added to one belongs in the other.
 *
 * One check is deliberately stricter than the save rather than a mirror of it —
 * a new message's start date, which the model does not constrain. It is marked
 * where it is made. Anything else stricter than the save is a bug, because the
 * panel then refuses something that would have been accepted.
 *
 * Pure by design: it takes builder state and returns a verdict, with no refs,
 * no requests and no React. Body emptiness is judged from the markup in state
 * rather than the editor's own async check, which stays authoritative at submit.
 */
export function getAppMessageReadiness(state: AppMessageBuilderState): Readiness {
    const variant = state.variants[state.selectedVariantIdx]
    const template = variant?.template
    const schedule = variant?.schedules?.[0]

    const isMessage = state.type === 'message'
    const checks: ReadinessCheck[] = []

    const name = state.details.name ?? template?.name
    checks.push({
        key: 'name',
        label: 'Named',
        satisfied: !!name?.trim(),
    })

    checks.push({
        key: 'channels',
        label: 'Assigned to at least one delivery channel',
        satisfied: (template?.channels?.length ?? 0) > 0,
    })

    checks.push({
        key: 'content',
        label: 'Message has content',
        satisfied: CONTENT_FIELDS.some((field) => !!template?.[field]?.enabled),
    })

    // Every togglable field requires its own content once it is switched on, so
    // each enabled field reports separately. A field that is off asks for
    // nothing and is left out of the list entirely.
    if (template?.image?.enabled) {
        checks.push({
            key: 'imageSource',
            label: 'Image chosen',
            satisfied: !!template.image.source?.trim(),
        })
    }

    if (template?.header?.enabled) {
        checks.push({
            key: 'headerValue',
            label: 'Header has text',
            satisfied: !!template.header.value?.trim(),
        })
    }

    if (template?.title?.enabled) {
        checks.push({
            key: 'titleValue',
            label: 'Title has text',
            satisfied: !!template.title.value?.trim(),
        })
    }

    if (template?.body?.enabled) {
        checks.push({
            key: 'bodyValue',
            label: 'Body has text',
            satisfied: hasVisibleText(template.body.value),
        })
    }

    for (const { key, cta, buttonName } of [
        { key: 'ctaPrimary', cta: template?.ctaPrimary, buttonName: 'Primary button' },
        { key: 'ctaSecondary', cta: template?.ctaSecondary, buttonName: 'Secondary button' },
    ] as const) {
        if (!cta?.enabled) {
            continue
        }

        const needsUrl = cta.action === AppMessageActionType.OPEN_URL

        // The colours and the action are checked as well as the text, because the
        // model requires all four of an enabled button and a check that looks at
        // fewer is a check that can call a rejected payload ready. Note that
        // without `action` the URL requirement would disappear on its own, since
        // `needsUrl` would be false — so an absent action has to fail here rather
        // than quietly relax what else is asked for.
        const complete =
            !!cta.label?.trim() && !!cta.action && !!cta.fgColor && !!cta.bgColor && (!needsUrl || !!cta.url?.trim())

        checks.push({
            key,
            label: needsUrl ? `${buttonName} has text and a URL` : `${buttonName} has text`,
            satisfied: complete,
        })
    }

    if (state.usingTemplate) {
        checks.push({
            key: 'template',
            label: 'A saved template is selected',
            satisfied: state.selectedTemplateId !== undefined,
        })
    }

    if (isMessage) {
        const reach = state.reachEstimateLoaded ? state.reachEstimate : undefined
        checks.push({
            key: 'audience',
            label:
                reach === undefined
                    ? 'Audience selected'
                    : `Audience reaches ~${GROUPED_NUMBER.format(reach)} app users`,
            satisfied: (schedule?.audience?.segmentIds?.length ?? 0) > 0,
        })

        const conditions = schedule?.conditions
        const customTriggerConfigured =
            !!conditions?.triggers?.key?.trim() && !!conditions?.triggers?.value?.toString().trim()
        checks.push({
            key: 'trigger',
            label: 'Trigger configured',
            satisfied: conditions?.type === AppMessageConditionType.CUSTOM ? customTriggerConfigured : true,
            revealRow: 'trigger',
        })

        // Only while creating. A message that is already running has a start
        // date in the past by definition, and editing one is a supported flow —
        // so requiring a future start on an edit makes a live message
        // permanently unsaveable, with changing its start the only way out.
        //
        // Nothing in the submit path rejects a past start either: the model's
        // `runDateStartUtc` carries `@Expose` and `IsNotEmptyString` and no date
        // constraint. This is therefore stricter than the save rather than a
        // mirror of it, and it is worth keeping only where the author is choosing
        // a start for the first time — a new message defaulting to a moment that
        // has already passed is a real mistake to catch.
        checks.push({
            key: 'startDate',
            label: 'Scheduled to start in the future',
            satisfied:
                !!schedule?.runDateStartUtc &&
                (state.mode !== 'create' || !dayjs(schedule.runDateStartUtc).isBefore(dayjs())),
            revealRow: 'schedule',
        })
    }

    const blockingCount = checks.filter((check) => !check.satisfied).length

    return { checks, blockingCount, ready: blockingCount === 0 }
}
